import React, { useMemo, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import moment from "moment";
import AcknowledgeAndVerifiedModel from "models/audit/AcknowledgeAndVerifiedModel";
import type { StoreReconciliationAuditShiftModel } from "models";
import type { StoreAuditSummaryRangeVerified } from "models/auditDashboard/StoreAuditSummaryRangeModel";
import {
  useAddManagerReconciliationNote,
  useAppendReconciliationVerificationNote,
  useVerifyDailyReconciliation,
} from "../../../audits/hooks/mutations/useReconciliationMutations";
import { formatUserRole } from "../../../users/utils/userRoleUtils";
import { useAuditDashboardStoreReconciliationAuditsByDate } from "../../hooks";
import { auditDashboardTheme } from "../../theme";
import { formatCurrency } from "../../utils";

interface ReconciliationReviewSectionProps {
  storeId?: string;
  reportDate: string;
  storeUserRole: string;
  canApproveReconciliation?: boolean;
  detail: AcknowledgeAndVerifiedModel | null | undefined;
  summaryVerified?: StoreAuditSummaryRangeVerified | null;
  embedded?: boolean;
}

const isVerified = (detail: AcknowledgeAndVerifiedModel | null | undefined, summaryVerified?: StoreAuditSummaryRangeVerified | null) =>
  detail?.isVerified === true || summaryVerified?.is_verified === true || summaryVerified?.audit_status === "approved" || summaryVerified?.status === "approved" || summaryVerified?.status === "verified";

const getAuditStatus = (detail: AcknowledgeAndVerifiedModel | null | undefined, summaryVerified?: StoreAuditSummaryRangeVerified | null) => {
  if (detail?.hasServerData) {
    return detail.auditStatus;
  }
  if (summaryVerified?.audit_status) {
    return summaryVerified.audit_status;
  }
  if (summaryVerified?.status === "verified") {
    return "approved";
  }
  if (summaryVerified?.status === "pending_verification") {
    return "needs_resolution";
  }
  if (summaryVerified?.status === "not_required") {
    return "matched";
  }
  return summaryVerified?.status || "matched";
};

const toDisplayText = (value: unknown): string => {
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatNoteTimestamp = (value?: unknown) => {
  if (!value) {
    return "";
  }
  const text = toDisplayText(value);
  return moment(text).isValid() ? moment(text).format("M/D/YY h:mm A") : "";
};

type TimelineNoteSource = "cashier" | "manager" | "verification";

type TimelineNote = {
  key: string;
  source: TimelineNoteSource;
  userName?: unknown;
  createdAt?: unknown;
  note: string;
  shiftContext?: CashierShiftContext;
};

type StructuredSystemInvestigation = {
  type: string;
  gamePacks: string[];
  impact: string;
};

type CashierShiftContext = {
  shiftLabel: string;
  timeRange?: string;
  dayOffset?: string;
  quantityVariance?: {
    text: string;
    value: number;
  };
  salesVariance?: {
    text: string;
    value: number;
  };
  backstockAddition?: string;
};

export const canShowApprovalForm = ({
  storeId,
  auditStatus,
  canVerify,
  isApprovalUser,
}: {
  storeId?: string;
  auditStatus: string;
  canVerify: boolean;
  isApprovalUser: boolean;
}) => Boolean(
  storeId
  && (auditStatus === "needs_resolution" || auditStatus === "pending_approval")
  && canVerify
  && isApprovalUser
);

const noteSourceConfig: Record<TimelineNoteSource, { roleLabel: string; color: string; bg: string }> = {
  cashier: {
    roleLabel: formatUserRole("operator"),
    color: auditDashboardTheme.colors.textNegative,
    bg: auditDashboardTheme.colors.redSoft,
  },
  manager: {
    roleLabel: formatUserRole("manager"),
    color: auditDashboardTheme.colors.textProgress,
    bg: auditDashboardTheme.colors.amberSoft,
  },
  verification: {
    roleLabel: "Reco Officer",
    color: "#047857",
    bg: auditDashboardTheme.colors.greenSoft,
  },
};

const noteTextFontFamily = 'Georgia, "Times New Roman", Times, serif';

const toTimestamp = (value?: unknown) => {
  if (!value) {
    return Number.MAX_SAFE_INTEGER;
  }
  const timestamp = Date.parse(toDisplayText(value));
  return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
};

export const parseSystemInvestigationNote = (note: string): StructuredSystemInvestigation[] | null => {
  const lines = note
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    return null;
  }

  const investigations = lines.map((line) => {
    const match = line.match(/^Type:\s*(.+?)\s*\|\s*Games\/Packs:\s*(.+?)\s*\|\s*Impact:\s*(.+)$/i);
    if (!match) {
      return null;
    }

    return {
      type: match[1].trim(),
      gamePacks: match[2].split(",").map((gamePack) => gamePack.trim()).filter(Boolean),
      impact: match[3].trim(),
    };
  });

  return investigations.every(Boolean)
    ? investigations as StructuredSystemInvestigation[]
    : null;
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value == null || value === "") {
    return null;
  }
  const nextValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(nextValue) ? nextValue : null;
};

const formatSignedCount = (value: number | null) => {
  if (value == null) {
    return "";
  }
  const prefix = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${prefix}${Math.abs(value).toLocaleString()}`;
};

const formatSignedCurrency = (value: number | null) => {
  if (value == null) {
    return "";
  }
  const prefix = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${prefix}${formatCurrency(Math.abs(value))}`;
};

const formatBackstockAddition = (packCount: number | null, ticketCount: number | null) => {
  const packs = Number(packCount ?? 0);
  const tickets = Number(ticketCount ?? 0);
  if (!packs && !tickets) {
    return "";
  }

  const parts = [];
  if (packs) {
    parts.push(`+${packs.toLocaleString()} ${packs === 1 ? "pack" : "packs"}`);
  }
  if (tickets) {
    parts.push(`+${tickets.toLocaleString()} ${tickets === 1 ? "ticket" : "tickets"}`);
  }
  return `Backstock ${parts.join(" / ")}`;
};

const getShiftDateTime = (dateValue?: string | null, timeValue?: string | null, fallbackDate?: string | null) => {
  if (!timeValue) {
    return null;
  }
  const dateText = dateValue || fallbackDate;
  if (!dateText) {
    return null;
  }
  const parsedDate = moment(String(dateText), "YYYY-MM-DD", true);
  const parsedTime = moment(String(timeValue), ["HH:mm:ss", "HH:mm", "h:mm A", "h:mm:ss A"], true);
  if (!parsedDate.isValid() || !parsedTime.isValid()) {
    return null;
  }
  return parsedDate
    .clone()
    .hour(parsedTime.hour())
    .minute(parsedTime.minute())
    .second(parsedTime.second());
};

const formatCashierShiftContext = (
  shift: StoreReconciliationAuditShiftModel | undefined,
  shiftNumber: number,
  reportDate: string
) => {
  if (!shift) {
    return undefined;
  }

  const shiftStart = getShiftDateTime(shift.shiftStartDate, shift.shiftStartTime, reportDate);
  const shiftEnd = getShiftDateTime(shift.shiftEndDate, shift.shiftEndTime, reportDate);
  const timeRange = shiftStart?.isValid()
    ? `${shiftStart.format("h:mm A")}-${shiftEnd?.isValid() ? shiftEnd.format("h:mm A") : ""}`
    : "";
  const dayOffset = shiftStart?.isValid() && shiftEnd?.isValid()
    ? shiftEnd.clone().startOf("day").diff(shiftStart.clone().startOf("day"), "days")
    : 0;
  const qtyVariance = formatSignedCount(toNumberOrNull(shift.scratcherQtyVariance));
  const salesVariance = formatSignedCurrency(toNumberOrNull(shift.scratcherSalesVariance));
  const backstockAddition = formatBackstockAddition(
    toNumberOrNull(shift.movedToBackstockCount),
    toNumberOrNull(shift.movedToBackstockTicketCount)
  );

  return {
    shiftLabel: `Shift ${shiftNumber}`,
    timeRange,
    dayOffset: dayOffset > 0 ? `+${dayOffset} ${dayOffset === 1 ? "Day" : "Days"}` : "",
    quantityVariance: qtyVariance ? {
      text: qtyVariance,
      value: toNumberOrNull(shift.scratcherQtyVariance) ?? 0,
    } : undefined,
    salesVariance: salesVariance ? {
      text: salesVariance,
      value: toNumberOrNull(shift.scratcherSalesVariance) ?? 0,
    } : undefined,
    backstockAddition: backstockAddition || undefined,
  };
};

export const buildTimelineNotes = (
  detail: AcknowledgeAndVerifiedModel | null | undefined,
  summaryVerified?: StoreAuditSummaryRangeVerified | null,
  shifts: StoreReconciliationAuditShiftModel[] = [],
  reportDate = ""
): TimelineNote[] => {
  const shiftByAuditId = new Map(shifts.map((shift, index) => [String(shift.auditId), { shift, shiftNumber: index + 1 }]));
  const cashierNotes = (detail?.cashierShiftNotes || []).flatMap((shiftNote, shiftIndex) => {
    const noteEntries = Array.isArray(shiftNote.notes) ? shiftNote.notes : [];
    const matchedShift = shiftByAuditId.get(String(shiftNote.audit_id || ""));
    return noteEntries
      .filter((entry: any) => entry?.type !== "system_investigation")
      .map((entry: any, entryIndex: number) => ({
      key: String(entry.id || `${shiftNote.audit_id || "shift"}-${shiftIndex}-${entryIndex}`),
      source: "cashier" as const,
      userName: entry.author_name || shiftNote.user_name,
      createdAt: entry.created_at || shiftNote.shift_end_time || shiftNote.created_at || shiftNote.audit_created_at,
      note: toDisplayText(entry.text || entry.note || entry).trim(),
      shiftContext: formatCashierShiftContext(matchedShift?.shift, matchedShift?.shiftNumber ?? shiftIndex + 1, reportDate),
      }));
  });

  const managerNotes = (detail?.managerNotes || []).map((note, index) => ({
    key: String(note.id || `manager-${note.created_at || index}`),
    source: "manager" as const,
    userName: note.user_name,
    createdAt: note.created_at,
    note: toDisplayText(note.note).trim(),
  }));

  const verificationNotes = (detail?.verificationNotes || []).map((note, index) => ({
    key: String(note.id || `verification-${note.created_at || index}`),
    source: "verification" as const,
    userName: note.user_name,
    createdAt: note.created_at,
    note: toDisplayText(note.note).trim(),
  }));

  if (!verificationNotes.length && summaryVerified?.note) {
    verificationNotes.push({
      key: "summary-verification-note",
      source: "verification",
      userName: summaryVerified.user_name || summaryVerified.verified_by_user_name,
      createdAt: summaryVerified.date_time || summaryVerified.verified_at,
      note: toDisplayText(summaryVerified.note).trim(),
    });
  }

  return [...cashierNotes, ...managerNotes, ...verificationNotes]
    .filter((entry) => entry.note)
    .sort((a, b) => toTimestamp(a.createdAt) - toTimestamp(b.createdAt));
};

export const latestSystemInvestigation = (
  detail: AcknowledgeAndVerifiedModel | null | undefined,
  reportDate?: string
) => {
  const entries = (detail?.cashierShiftNotes || []).flatMap((shiftNote) =>
    (Array.isArray(shiftNote.notes) ? shiftNote.notes : [])
      .filter((entry: any) =>
        entry?.type === "system_investigation"
        && (!reportDate || !entry?.metadata?.report_date
          || entry.metadata.report_date === reportDate)
      )
  );
  const sortedEntries = entries.sort(
    (left: any, right: any) =>
      toTimestamp(left?.created_at) - toTimestamp(right?.created_at)
  );
  return sortedEntries[sortedEntries.length - 1];
};

const renderSystemInvestigationNote = (note: string) => {
  const investigations = parseSystemInvestigationNote(note);
  if (!investigations) {
    return null;
  }

  return (
    <Stack spacing={1}>
      {investigations.map((investigation) => (
        <Box
          key={`${investigation.type}-${investigation.gamePacks.join("-")}-${investigation.impact}`}
          sx={{
            bgcolor: auditDashboardTheme.colors.surface3,
            border: "1px solid",
            borderColor: auditDashboardTheme.colors.lineSoft,
            borderRadius: "8px",
            px: 1.25,
            py: 1,
          }}
        >
          <Typography
            sx={{
              color: auditDashboardTheme.colors.text,
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1.35,
            }}
          >
            {investigation.type}
          </Typography>
          <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 0.55, mt: 0.65 }}>
            <Typography
              component="span"
              sx={{
                color: auditDashboardTheme.colors.description,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.05em",
                mr: 0.2,
                textTransform: "uppercase",
              }}
            >
              Games / packs
            </Typography>
            {investigation.gamePacks.map((gamePack) => (
              <Box
                component="span"
                key={gamePack}
                sx={{
                  bgcolor: auditDashboardTheme.colors.blueSoft,
                  borderRadius: "5px",
                  color: auditDashboardTheme.colors.blue,
                  fontSize: 12,
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 600,
                  lineHeight: 1.3,
                  px: 0.7,
                  py: 0.25,
                }}
              >
                {gamePack}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: 0.7, mt: 0.7 }}>
            <Typography
              component="span"
              sx={{
                color: auditDashboardTheme.colors.textProgress,
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.45,
              }}
            >
              Impact
            </Typography>
            <Typography
              component="span"
              sx={{
                color: auditDashboardTheme.colors.textMutedStrong,
                fontSize: 12.5,
                lineHeight: 1.45,
              }}
            >
              {investigation.impact}
            </Typography>
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

const renderNoteTimeline = (notes: TimelineNote[]) => {
  if (!notes.length) {
    return (
      <Typography sx={{ color: auditDashboardTheme.colors.description, fontSize: 13, lineHeight: 1.45 }}>
        No notes recorded.
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      {notes.map((note, index) => {
        const config = noteSourceConfig[note.source];
        const userName = toDisplayText(note.userName);
        const timestamp = formatNoteTimestamp(note.createdAt);
        const sourceLabel = config.roleLabel;
        return (
          <Box
            key={note.key}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "76px minmax(0, 1fr)", sm: "96px minmax(0, 1fr) minmax(120px, auto)" },
              columnGap: { xs: 1, sm: 1.5 },
              alignItems: "start",
              minWidth: 0,
              pt: index === 0 ? 0 : 1.1,
              pb: index === notes.length - 1 ? 0 : 1.1,
              borderTop: index === 0 ? 0 : "1px solid",
              borderColor: auditDashboardTheme.colors.lineSoft,
            }}
          >
            <Box
              aria-label={sourceLabel}
              sx={{
                color: config.color,
                alignSelf: "start",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.06em",
                lineHeight: 1.2,
                textTransform: "uppercase",
              }}
            >
              {sourceLabel}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: auditDashboardTheme.colors.text,
                  fontFamily: noteTextFontFamily,
                  fontSize: 14.5,
                  fontWeight: 400,
                  lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                }}
              >
                {note.note}
              </Typography>
              {note.shiftContext ? (
                <Box
                  sx={{
                    alignItems: "center",
                    color: auditDashboardTheme.colors.description,
                    display: "flex",
                    flexWrap: "wrap",
                    fontSize: 12,
                    fontWeight: 500,
                    gap: 0.65,
                    lineHeight: 1.35,
                    mt: 0.45,
                  }}
                >
                  <Box component="span">{note.shiftContext.shiftLabel}</Box>
                  {note.shiftContext.timeRange ? (
                    <>
                      <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong }}>|</Box>
                      <Box component="span" sx={{ color: auditDashboardTheme.colors.text, fontWeight: 700 }}>
                        {note.shiftContext.timeRange}
                      </Box>
                      {note.shiftContext.dayOffset ? (
                        <Box component="span" sx={{ color: auditDashboardTheme.colors.text, fontWeight: 700 }}>
                          {note.shiftContext.dayOffset}
                        </Box>
                      ) : null}
                    </>
                  ) : null}
                  {note.shiftContext.quantityVariance ? (
                    <>
                      <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong }}>|</Box>
                      <Box
                        component="span"
                        sx={{
                          color: note.shiftContext.quantityVariance.value > 0
                            ? auditDashboardTheme.colors.textPositive
                            : note.shiftContext.quantityVariance.value < 0
                              ? auditDashboardTheme.colors.textNegative
                              : auditDashboardTheme.colors.description,
                        }}
                      >
                        Qty {note.shiftContext.quantityVariance.text}
                      </Box>
                    </>
                  ) : null}
                  {note.shiftContext.salesVariance ? (
                    <>
                      <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong }}>|</Box>
                      <Box
                        component="span"
                        sx={{
                          color: note.shiftContext.salesVariance.value > 0
                            ? auditDashboardTheme.colors.textPositive
                            : note.shiftContext.salesVariance.value < 0
                              ? auditDashboardTheme.colors.textNegative
                              : auditDashboardTheme.colors.description,
                        }}
                      >
                        {note.shiftContext.salesVariance.text}
                      </Box>
                    </>
                  ) : null}
                  {note.shiftContext.backstockAddition ? (
                    <>
                      <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong }}>|</Box>
                      <Box component="span" sx={{ color: auditDashboardTheme.colors.textProgress }}>
                        {note.shiftContext.backstockAddition}
                      </Box>
                    </>
                  ) : null}
                </Box>
              ) : null}
            </Box>
            {userName || timestamp ? (
              <Box
                sx={{
                  gridColumn: { xs: "2 / 3", sm: "auto" },
                  mt: { xs: 0.25, sm: 0 },
                  textAlign: { xs: "left", sm: "right" },
                  minWidth: 0,
                  alignSelf: "end",
                }}
              >
                {userName ? (
                  <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 12, fontWeight: 400, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                    {userName}
                  </Typography>
                ) : null}
                {timestamp ? (
                  <Typography sx={{ color: auditDashboardTheme.colors.description, fontSize: 12, fontWeight: 400, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                    {timestamp}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Stack>
  );
};

const noteInputSx = {
  "& .MuiInputBase-root": {
    borderRadius: "8px",
    bgcolor: auditDashboardTheme.colors.surface,
  },
  "& .MuiInputBase-input": {
    color: auditDashboardTheme.colors.text,
    fontFamily: noteTextFontFamily,
    fontSize: 14.5,
    fontWeight: 400,
    lineHeight: 1.55,
  },
  "& textarea::placeholder": {
    color: auditDashboardTheme.colors.description,
    opacity: 1,
    fontWeight: 500,
  },
};

export default function ReconciliationReviewSection({
  storeId,
  reportDate,
  storeUserRole,
  canApproveReconciliation = false,
  detail,
  summaryVerified,
  embedded = false,
}: ReconciliationReviewSectionProps) {
  const [verificationNote, setVerificationNote] = useState("");
  const [managerNote, setManagerNote] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [expanded, setExpanded] = useState(false);
  const verifyMutation = useVerifyDailyReconciliation(storeId || "");
  const appendVerificationNoteMutation = useAppendReconciliationVerificationNote(storeId || "");
  const managerNoteMutation = useAddManagerReconciliationNote(storeId || "");
  const shiftsQuery = useAuditDashboardStoreReconciliationAuditsByDate(storeId, reportDate, Boolean(storeId && reportDate));
  const auditStatus = getAuditStatus(detail, summaryVerified);
  const verified = isVerified(detail, summaryVerified) || auditStatus === "approved";
  const canVerify = detail?.hasServerData
    ? detail.canVerify
    : summaryVerified?.can_verify === true;
  const canAddManagerNote = detail?.hasServerData
    ? detail.canAddManagerNote
    : summaryVerified?.can_add_manager_note === true;
  const isApprovalUser = storeUserRole === "owner" ||
    storeUserRole === "reco_officer" ||
    storeUserRole === "reconciliation_manager" ||
    (storeUserRole === "manager" && canApproveReconciliation);
  const showVerifyForm = canShowApprovalForm({ storeId, auditStatus, canVerify, isApprovalUser });
  const showManagerNoteForm = Boolean(storeId && auditStatus === "needs_resolution" && canAddManagerNote && storeUserRole === "manager" && !canApproveReconciliation);
  const showVerificationNoteForm = Boolean(storeId && auditStatus === "approved" && isApprovalUser);
  const statusColor =
    auditStatus === "approved"
      ? "#047857"
      : auditStatus === "pending_approval"
        ? auditDashboardTheme.colors.textProgress
        : auditDashboardTheme.colors.textNegative;
  const statusBackground =
    auditStatus === "approved"
      ? auditDashboardTheme.colors.greenSoft
      : auditStatus === "pending_approval"
        ? auditDashboardTheme.colors.amberSoft
        : auditDashboardTheme.colors.redSoft;
  const StatusIcon = verified ? CheckCircleOutlineIcon : ErrorOutlineIcon;
  const statusText =
    auditStatus === "approved"
      ? "Reconciled"
      : auditStatus === "pending_approval"
        ? "Pending Approval"
        : auditStatus === "needs_resolution"
          ? "Investigation Pending"
          : "Matched";
  const verifierText = useMemo(() => {
    const userName = detail?.verifiedByUserName || summaryVerified?.user_name || summaryVerified?.verified_by_user_name || "";
    const dateTime = detail?.verifiedAt || summaryVerified?.date_time || summaryVerified?.verified_at || "";
    return [userName, formatNoteTimestamp(dateTime)].filter(Boolean).join(" | ");
  }, [detail, summaryVerified]);
  const timelineNotes = useMemo(
    () => buildTimelineNotes(detail, summaryVerified, shiftsQuery.data?.shifts ?? [], reportDate),
    [detail, reportDate, shiftsQuery.data?.shifts, summaryVerified]
  );
  const systemInvestigationEntry = latestSystemInvestigation(detail, reportDate);
  const systemInvestigation = toDisplayText(systemInvestigationEntry?.text);
  const systemInvestigationGeneratedAt = toDisplayText(
    systemInvestigationEntry?.created_at
  );
  const showBody = !embedded || expanded;

  const submitManagerNote = async () => {
    setErrorMessage("");
    const response = await managerNoteMutation.mutateAsync({
      reconciliation_date: reportDate,
      note: managerNote,
    });
    if (!response.success) {
      setErrorMessage(String(response.error || "Unable to save manager note."));
      return;
    }
    setManagerNote("");
  };

  const submitVerification = async () => {
    setErrorMessage("");
    const response = await verifyMutation.mutateAsync({
      reconciliation_date: reportDate,
      verification_note: verificationNote,
    });
    if (!response.success) {
      setErrorMessage(String(response.error || "Unable to verify daily reconciliation."));
      return;
    }
    setVerificationNote("");
  };

  const submitVerificationNote = async () => {
    setErrorMessage("");
    const response = await appendVerificationNoteMutation.mutateAsync({
      reconciliation_date: reportDate,
      verification_note: verificationNote,
    });
    if (!response.success) {
      setErrorMessage(String(response.error || "Unable to save verification note."));
      return;
    }
    setVerificationNote("");
  };

  const composer = showManagerNoteForm ? (
    <Box sx={{ display: "grid", gap: 1 }}>
      <TextField
        placeholder="Add resolution note"
        size="small"
        multiline
        minRows={2}
        value={managerNote}
        onChange={(event) => setManagerNote(event.target.value)}
        fullWidth
        sx={noteInputSx}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={submitManagerNote}
          disabled={!managerNote.trim() || managerNoteMutation.isPending}
          sx={{ borderRadius: "8px", px: 2, textTransform: "none", fontSize: 13, fontWeight: 800 }}
        >
          Add Resolution
        </Button>
      </Box>
    </Box>
  ) : showVerifyForm ? (
    <Box sx={{ display: "grid", gap: 1 }}>
      <TextField
        placeholder="Add approval note"
        size="small"
        multiline
        minRows={2}
        value={verificationNote}
        onChange={(event) => setVerificationNote(event.target.value)}
        fullWidth
        sx={noteInputSx}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={submitVerification}
          disabled={!verificationNote.trim() || verifyMutation.isPending}
          sx={{ borderRadius: "8px", px: 2, textTransform: "none", fontSize: 13, fontWeight: 800 }}
        >
          Approve & Reconcile
        </Button>
      </Box>
    </Box>
  ) : showVerificationNoteForm ? (
    <Box sx={{ display: "grid", gap: 1 }}>
      <TextField
        placeholder="Add verification note"
        size="small"
        multiline
        minRows={2}
        value={verificationNote}
        onChange={(event) => setVerificationNote(event.target.value)}
        fullWidth
        sx={noteInputSx}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          onClick={submitVerificationNote}
          disabled={!verificationNote.trim() || appendVerificationNoteMutation.isPending}
          sx={{
            borderRadius: "8px",
            px: 2,
            borderColor: auditDashboardTheme.colors.lineStrong,
            color: auditDashboardTheme.colors.text,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          Add Note
        </Button>
      </Box>
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        display: "grid",
        gap: showBody ? 0 : 0,
        p: embedded ? 0 : { xs: 1.5, sm: 2 },
        border: embedded ? 0 : "1px solid",
        borderColor: auditDashboardTheme.colors.line,
        borderRadius: embedded ? 0 : "8px",
        bgcolor: embedded ? "transparent" : auditDashboardTheme.colors.surface,
      }}
    >
      <Box
        role={embedded ? "button" : undefined}
        tabIndex={embedded ? 0 : undefined}
        aria-expanded={embedded ? expanded : undefined}
        onClick={embedded ? () => setExpanded((current) => !current) : undefined}
        onKeyDown={embedded ? (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setExpanded((current) => !current);
          }
        } : undefined}
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          px: embedded ? { xs: 1.75, sm: 3 } : 0,
          py: embedded ? "10px" : 0,
          bgcolor: embedded ? statusBackground : "transparent",
          borderBottomLeftRadius: embedded && !expanded ? "8px" : 0,
          borderBottomRightRadius: embedded && !expanded ? "8px" : 0,
          cursor: embedded ? "pointer" : "default",
          outline: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: statusBackground,
            flexShrink: 0,
          }}
        >
          <StatusIcon sx={{ color: statusColor, fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0, display: "grid", gap: 0.25 }}>
          <Typography sx={{ color: statusColor, fontSize: 15, fontWeight: 800, lineHeight: 1.15 }}>
            {statusText}
          </Typography>
          {verifierText ? (
            <Typography sx={{ color: auditDashboardTheme.colors.description, fontSize: 12, fontWeight: 600, lineHeight: 1.25, overflowWrap: "anywhere" }}>
            {verifierText}
          </Typography>
        ) : null}
        </Box>
        </Box>
        {embedded ? (
          <ChevronRightIcon
            aria-hidden="true"
            sx={{
              color: auditDashboardTheme.colors.blue,
              fontSize: 20,
              transform: expanded ? "rotate(90deg)" : "none",
              transition: "transform 160ms ease",
            }}
          />
        ) : null}
      </Box>

      {showBody ? (
        <>
          {systemInvestigation ? (
            <Box
              aria-label="System investigation"
              sx={{
                px: embedded ? { xs: 1.75, sm: 3 } : 0,
                py: 1.5,
                bgcolor: embedded ? auditDashboardTheme.colors.surface : "transparent",
                borderBottom: "1px solid",
                borderColor: auditDashboardTheme.colors.lineSoft,
              }}
            >
              <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 13, fontWeight: 800, mb: 0.75 }}>
                System Investigation
              </Typography>
              {systemInvestigationGeneratedAt ? (
                <Typography sx={{ color: auditDashboardTheme.colors.description, fontSize: 11.5, mb: 0.75 }}>
                  Generated {formatNoteTimestamp(systemInvestigationGeneratedAt)}
                </Typography>
              ) : null}
              {renderSystemInvestigationNote(systemInvestigation) || (
                <Typography
                  component="div"
                  sx={{
                    color: auditDashboardTheme.colors.text,
                    fontFamily: noteTextFontFamily,
                    fontSize: 13.5,
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {systemInvestigation}
                </Typography>
              )}
            </Box>
          ) : null}
          <Box
            sx={{
              px: embedded ? { xs: 1.75, sm: 3 } : 0,
              pt: embedded ? 1.25 : 0.25,
              pb: embedded ? 1.25 : 0,
              bgcolor: embedded ? auditDashboardTheme.colors.surface : "transparent",
            }}
          >
            {renderNoteTimeline(timelineNotes)}
          </Box>

          {composer ? (
            <Box
              sx={{
                px: embedded ? { xs: 1.75, sm: 3 } : 0,
                pt: 1.25,
                pb: embedded ? 1.5 : 0,
                bgcolor: embedded ? auditDashboardTheme.colors.surface : "transparent",
                borderTop: "1px solid",
                borderColor: auditDashboardTheme.colors.lineSoft,
              }}
            >
              {composer}
            </Box>
          ) : null}

          {errorMessage ? (
            <Typography sx={{ color: auditDashboardTheme.colors.textNegative, fontSize: 13, fontWeight: 800, px: embedded ? { xs: 1.75, sm: 3 } : 0, pb: embedded ? 1.25 : 0 }}>
              {errorMessage}
            </Typography>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}
